import { FC, ReactNode, useEffect, useId, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Drawer as MantineDrawer, CloseButton, useMantineEnv } from '@mantine/core';
import clsx from 'clsx';
import type { ModalCloseReason } from '../../types/ModalCloseReason';
import { useLatest } from '../../hooks/useLatest';
import { pushModalLayer } from '../../lib/modalLayers';
import { usePortalTarget } from '../../provider/UiContext';
import { FieldA11yContext } from '../../context/FieldA11yContext';
import { DialogAria } from '../Modal/DialogAria';
import './Drawer.scss';

export interface IDrawerProps {
    /** Контент панели */
    children: ReactNode;
    /** Закрытие панели: крестик, Escape и клик по подложке. Без него панель закрывается только своими кнопками. */
    onClose?: (reason: ModalCloseReason) => void;
    /** Контент шапки (слева от крестика). По умолчанию — заголовок `<h2>`, им же подписана панель; с
     *  `aria-labelledby` — своя разметка шапки (заголовок со своими кнопками) без обёртки `<h2>`. */
    header?: ReactNode;
    /** Контент футера */
    footer?: ReactNode;
    /** id заголовка в своей разметке шапки (`header`) или содержимого: панель подписана им, а `header` рисуется
     *  без обёртки `<h2>`. */
    'aria-labelledby'?: string;
    /** id описания панели (через пробел — несколько), которое диктор читает вслед за именем. По умолчанию описания
     *  нет — содержимое читается по мере перехода по нему. */
    'aria-describedby'?: string;
    /** Ширина панели; по умолчанию min(640px, 100vw) */
    width?: number | string;
    /** Сторона выезда @default right */
    position?: 'right' | 'left';
    /** Отступ панели от краёв экрана (число — px): панель «плавает» над страницей, а не прилегает к краю.
     *  Другой отступ сверху (под шапкой приложения) — правилом раскладки на `.rf-drawer__container`. */
    offset?: number | string;
    /** Скругление панели (с `offset`) */
    radius?: number | string;
    /** Класс на root-элементе */
    className?: string;
    /** Класс на обёртке содержимого `.rf-drawer__content` — у неё отступы и прокрутка панели. Содержимому со
     *  своей прокруткой и раскладкой их снимают этим классом. */
    contentClassName?: string;
    /** Закрывать по Escape (при `onClose`). Escape закрывает только окно, в котором фокус (окно поверх панели —
     *  первым); погашенный вложенным контролом (открытый список, меню, поповер) панель не закрывает.
     * @default true
     */
    closeOnEscape?: boolean;
    /** Закрывать по клику вне панели @default true */
    closeOnClickOutside?: boolean;
    /** Заблокировать все средства закрытия (крестик, Escape, клик снаружи) — на время сохранения и других
     *  действий, которые нельзя оборвать. Крестик, показанный явно (`withCloseButton`), виден, но недоступен.
     * @default false
     */
    closeDisabled?: boolean;
    /** Затемнять фон. Панель просмотра обычно не блокирует работу со страницей. */
    withOverlay?: boolean;
    /** Удерживать фокус в панели. `false` — немодальная панель над страницей, с которой можно работать: фокус
     *  при открытии переводится на панель, после закрытия возвращается на элемент, открывший её.
     * @default true
     */
    trapFocus?: boolean;
    /** Показывать крестик в шапке. По умолчанию — true, если задан onClose. */
    withCloseButton?: boolean;
    zIndex?: number;
    /** Управляемая открытость. Без пропа панель сама играет появление на маунте
     *  (закрытие тогда не анимируется — вызывающий вырывает поддерево). */
    opened?: boolean;
    /** Вызывается после завершения exit-анимации управляемой панели. */
    onExitTransitionEnd?: () => void;
}

const DRAWER_TRANSITION = {
    transition: 'slide-left',
    duration: 220,
    timingFunction: 'cubic-bezier(0.2, 0.7, 0.2, 1)',
} as const;

const noop = () => undefined;

const CLASS_NAMES = {
    overlay: 'rf-drawer__bg',
    content: 'rf-drawer__container',
    header: 'rf-drawer__header',
    title: 'rf-drawer__title',
    close: 'rf-drawer__close-button',
    body: 'rf-drawer__body',
};

const dialogOf = (drawerId: string) =>
    document.getElementById(drawerId)?.querySelector<HTMLElement>('.rf-drawer__container') ?? null;

/** Немодальная панель (`trapFocus={false}`): ловушки Mantine нет — фокус переводится на панель при её вставке в
 *  документ, если он ещё не внутри (поле с `autoFocus`). `aria-modal` Mantine ставит всегда — у немодальной панели
 *  его нет: остальная страница для диктора не скрыта. */
const FocusOnOpen: FC<{ drawerId: string }> = ({ drawerId }) => {
    useLayoutEffect(() => {
        const dialog = dialogOf(drawerId);
        dialog?.removeAttribute('aria-modal');
        if (dialog && !dialog.contains(document.activeElement)) dialog.focus({ preventScroll: true });
    }, [drawerId]);
    return null;
};

/**
 * Боковая панель (выезжает из-за края экрана) — фасад Mantine Drawer с проектной
 * поверхностью, как @atoms/Modal для диалогов. Для карточек сущностей, которые
 * должны открываться, не уводя пользователя со списка.
 */
const Drawer: FC<IDrawerProps> = ({
    children,
    onClose,
    header,
    footer,
    'aria-labelledby': labelledBy,
    'aria-describedby': describedBy,
    width,
    position = 'right',
    offset,
    radius,
    className = '',
    contentClassName,
    closeOnEscape = true,
    closeOnClickOutside = true,
    closeDisabled = false,
    withOverlay = false,
    trapFocus = true,
    withCloseButton,
    zIndex = 1000,
    opened,
    onExitTransitionEnd,
}) => {
    const portalTarget = usePortalTarget();
    const instantTransitions = useMantineEnv() === 'test';
    // как в Modal: Mantine пропускает переход, если mounted=true уже на первом
    // рендере — стартуем закрытыми и открываемся следующим кадром; в тестовом окружении переходов нет
    const [selfOpened, setSelfOpened] = useState(instantTransitions);
    useEffect(() => {
        if (instantTransitions) return;
        const raf = requestAnimationFrame(() => setSelfOpened(true));
        return () => cancelAnimationFrame(raf);
    }, [instantTransitions]);
    const wantOpened = opened ?? true;
    const isOpened = opened ?? selfOpened;

    // Кто открыл панель — на рендере, пока фокус ещё на нём (нужен немодальной панели: Mantine без ловушки фокус
    // не возвращает).
    const [opening, setOpening] = useState(() => ({
        opened: wantOpened,
        opener: wantOpened ? document.activeElement : null,
    }));
    if (opening.opened !== wantOpened) {
        setOpening({ opened: wantOpened, opener: wantOpened ? document.activeElement : opening.opener });
    }
    const { opener } = opening;

    // id корня Mantine (`ModalBase`): по нему слой окна находит панель в DOM
    const drawerId = useId();
    const canClose = Boolean(onClose) && !closeDisabled;
    const showCloseButton = withCloseButton ?? canClose;
    const ownHeader = Boolean(labelledBy);

    const escape = useLatest(canClose && closeOnEscape ? onClose : undefined);
    useEffect(() => {
        if (!wantOpened) return;
        return pushModalLayer({
            root: () => document.getElementById(drawerId),
            dialog: () => dialogOf(drawerId),
            onEscape: () => {
                const close = escape.current;
                close?.('escape');
                return Boolean(close);
            },
        });
    }, [wantOpened, escape, drawerId]);

    useEffect(() => {
        if (!wantOpened || trapFocus) return;
        return () => {
            const active = document.activeElement;
            if (
                (!active || active === document.body || document.getElementById(drawerId)?.contains(active)) &&
                opener instanceof HTMLElement &&
                opener !== document.body &&
                opener.isConnected
            ) {
                opener.focus({ preventScroll: true });
            }
        };
    }, [wantOpened, trapFocus, opener, drawerId]);

    const hasHeader = Boolean(header) || showCloseButton;

    const drawer = (
        <MantineDrawer.Root
            id={drawerId}
            opened={isOpened}
            onClose={canClose ? () => onClose?.('outside') : noop}
            position={position}
            size={width ?? 'min(640px, 100vw)'}
            offset={offset}
            radius={radius}
            padding={0}
            closeOnEscape={false}
            closeOnClickOutside={canClose && closeOnClickOutside}
            trapFocus={trapFocus}
            lockScroll={withOverlay}
            zIndex={zIndex}
            withinPortal={!portalTarget}
            transitionProps={DRAWER_TRANSITION}
            onExitTransitionEnd={onExitTransitionEnd}
            classNames={{
                root: clsx('rf-drawer', offset !== undefined && 'rf-drawer--floating', className),
                ...CLASS_NAMES,
            }}
            data-testid="rf-drawer"
        >
            {withOverlay && <MantineDrawer.Overlay />}
            <MantineDrawer.Content>
                {hasHeader && (
                    <MantineDrawer.Header>
                        {header &&
                            (ownHeader ? (
                                <div className="rf-drawer__header-content">{header}</div>
                            ) : (
                                <MantineDrawer.Title>{header}</MantineDrawer.Title>
                            ))}
                        {showCloseButton && (
                            <CloseButton
                                className={CLASS_NAMES.close}
                                aria-label="Закрыть"
                                disabled={closeDisabled}
                                onClick={() => canClose && onClose?.('close-button')}
                            />
                        )}
                    </MantineDrawer.Header>
                )}
                <MantineDrawer.Body>
                    <div className="rf-drawer__wrapper">
                        <div className={clsx('rf-drawer__content', contentClassName)}>{children}</div>
                        {footer && <div className="rf-drawer__footer">{footer}</div>}
                    </div>
                    <DialogAria rootId={drawerId} labelledBy={labelledBy} describedBy={describedBy} />
                    {!trapFocus && <FocusOnOpen drawerId={drawerId} />}
                </MantineDrawer.Body>
            </MantineDrawer.Content>
        </MantineDrawer.Root>
    );
    // Панель, открытая из поля группы, не наследует её подпись и сообщение — у полей панели свои
    const scoped = <FieldA11yContext value={null}>{drawer}</FieldA11yContext>;
    // Хост области (дизайнер маршрутов) — свой портал, как у Modal.
    return portalTarget ? createPortal(scoped, portalTarget) : scoped;
};

export default Drawer;
